import { useMemo } from "react";
import type { ExamTarget, ScoreBand } from "../lib/api.js";

const EXAMS: { id: ExamTarget; label: string; desc: string }[] = [
  { id: "SAT", label: "SAT", desc: "Digital, adaptive" },
  // Directly after SAT, because that is where somebody looks for it, and as its
  // own choice rather than a note under SAT: a sophomore sitting the PSAT next
  // month should not have to know the two are related to declare it. What they
  // then practice IS the SAT content, which targetModuleSections encodes.
  { id: "PSAT", label: "PSAT", desc: "PSAT/NMSQT and PSAT 10" },
  { id: "ACT", label: "ACT", desc: "Shorter format" },
  { id: "AP", label: "AP", desc: "Free response" },
  { id: "APPS", label: "Essays", desc: "Common App and supplements" },
];

const BANDS: ScoreBand[] = ["1200+", "1300+", "1400+", "1500+", "1550+"];

export type TargetPickerProps = {
  exams: ExamTarget[];
  band: ScoreBand | null;
  date: string | null;
  onExamsChange: (next: ExamTarget[]) => void;
  onBandChange: (next: ScoreBand | null) => void;
  onDateChange: (next: string | null) => void;
};

/**
 * Shared between the onboarding Target screen (02/03) and the
 * profile-drawer Goal Switch modal. State is controlled by the parent
 * so each surface can decide its own submit/save copy.
 *
 * Design: per `screens-a.jsx` GoalSelectionScreen — multi-select
 * exam chips with amber-border-when-armed, then a band radio row,
 * then an optional test-date display.
 */
export function TargetPicker({
  exams,
  band,
  date,
  onExamsChange,
  onBandChange,
  onDateChange,
}: TargetPickerProps) {
  function toggle(id: ExamTarget) {
    const set = new Set(exams);
    set.has(id) ? set.delete(id) : set.add(id);
    onExamsChange(Array.from(set));
  }

  // Next 9 months as tappable chips (clearer + reliable on mobile vs a
  // native <input type="month">, which renders inconsistently and felt
  // unresponsive). Value is "YYYY-MM"; tapping the selected chip clears it.
  const monthOptions = useMemo(() => {
    const now = new Date();
    const out: { value: string; label: string }[] = [];
    for (let i = 0; i < 9; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      out.push({ value, label });
    }
    return out;
  }, []);

  return (
    <div className="app-stack" style={{ gap: "var(--sp-5)" }}>
      <div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.16em",
            color: "var(--fg-2)",
            marginBottom: 12,
            textTransform: "uppercase",
          }}
        >
          Exams · multi-select
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          {EXAMS.map((o) => {
            const armed = exams.includes(o.id);
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => toggle(o.id)}
                style={{
                  padding: "14px 12px",
                  background: armed
                    ? "var(--accent-amber-12)"
                    : "var(--bg-1)",
                  border: `${armed ? 2 : 1}px solid ${armed ? "var(--accent-amber)" : "var(--border-line)"}`,
                  borderRadius: 8,
                  textAlign: "left",
                  cursor: "pointer",
                  position: "relative",
                  color: "var(--fg-0)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    fontWeight: 600,
                    color: armed ? "var(--accent-amber)" : "var(--fg-0)",
                    letterSpacing: "0.08em",
                  }}
                >
                  {o.label}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 12,
                    color: "var(--fg-2)",
                    marginTop: 4,
                  }}
                >
                  {o.desc}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.16em",
            color: "var(--fg-2)",
            marginBottom: 10,
            textTransform: "uppercase",
          }}
        >
          Target band
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {BANDS.map((b) => {
            const armed = band === b;
            return (
              <button
                key={b}
                type="button"
                onClick={() => onBandChange(armed ? null : b)}
                style={{
                  padding: "8px 14px",
                  background: armed ? "var(--accent-amber)" : "var(--bg-1)",
                  border: `1px solid ${armed ? "var(--accent-amber)" : "var(--border-line)"}`,
                  borderRadius: 6,
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  fontWeight: armed ? 600 : 500,
                  color: armed ? "#0B0F12" : "var(--fg-1)",
                  cursor: "pointer",
                }}
              >
                {b}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.16em",
            color: "var(--fg-2)",
            marginBottom: 10,
            textTransform: "uppercase",
          }}
        >
          Test date · optional
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {monthOptions.map((m) => {
            const armed = date === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => onDateChange(armed ? null : m.value)}
                style={{
                  padding: "8px 12px",
                  background: armed ? "var(--accent-amber)" : "var(--bg-1)",
                  border: `1px solid ${armed ? "var(--accent-amber)" : "var(--border-line)"}`,
                  borderRadius: 6,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  fontWeight: armed ? 600 : 500,
                  color: armed ? "#0B0F12" : "var(--fg-1)",
                  cursor: "pointer",
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
